import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CANVAS_W, CANVAS_H, TEMPLATE_LIST, TEMPLATE_MAP, drawBranding } from "@/lib/poster-templates";
import {
    STICKER_LIBRARY, STICKER_PRESETS, PRESET_COLORS,
    MAX_STICKERS, MIN_SCALE, MAX_SCALE, DEFAULT_SCALE,
    drawSticker, stickerHit,
} from "@/lib/sticker-lib";
import { publicAssetUrl, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Palette, Upload, Sparkles, CheckCircle2, Type as TypeIcon, Sticker as StickerIcon, Trash2, X } from "lucide-react";

const FONT_STYLES = [
    { key: "BOLD", label: "Bold" },
    { key: "MODERN", label: "Modern" },
    { key: "PLAYFUL", label: "Playful" },
];

/** Load an image as a Promise (uses crossOrigin to keep canvas untainted). */
const loadImg = (src) =>
    new Promise((resolve, reject) => {
        if (!src) return resolve(null);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

/**
 * Full Poster Studio: template gallery + live canvas + controls + auto-branded
 * publish. On publish, exports a PNG blob, uploads it to /api/uploads, and
 * calls `onGenerated(uploadedPosterUrl)` so the parent wizard can wire it into
 * the offer create request.
 */
export function PosterStudio({
    initialDiscount,        // e.g. "20% OFF"
    profile,                // { business_name, business_district, shop_logo_url, shop_photo_url }
    onGenerated,            // (url) => void — called after successful upload
    apiClient,              // pass the axios `api` instance so we don't re-import here
}) {
    const canvasRef = useRef(null);
    const [templateId, setTemplateId] = useState(TEMPLATE_LIST[0].id);
    const tpl = TEMPLATE_MAP[templateId];
    const [productPhoto, setProductPhoto] = useState(null);   // HTMLImageElement
    const [productSrc, setProductSrc] = useState("");        // preview url for <img>
    const [state, setState] = useState({
        headline: tpl.defaults.headline,
        sub: tpl.defaults.sub,
        discount: initialDiscount || "",
        accent: tpl.defaults.accent,
        font: tpl.defaults.font,
        zoom: 1,
    });
    // Bug fix (Phase 8 P1): track whether the merchant has manually edited the
    // discount input. Until they do, keep it synced with `initialDiscount` from
    // the wizard's Step 1 so returning to Step 2 after editing the discount
    // still shows the correct value on the canvas.
    const discountEditedRef = useRef(false);
    const setDiscount = (value) => {
        discountEditedRef.current = true;
        setState((s) => ({ ...s, discount: value }));
    };
    const [wordmark, setWordmark] = useState(null);
    const [shopLogo, setShopLogo] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [generatedUrl, setGeneratedUrl] = useState(null);
    // Phase 10 — Sticker layer
    const [stickers, setStickers] = useState([]);
    const [selectedStickerId, setSelectedStickerId] = useState(null);
    const dragRef = useRef({ id: null, offsetX: 0, offsetY: 0, moved: false });

    // Load the OfferNest wordmark + the merchant's shop logo (or fallback) once
    useEffect(() => {
        loadImg("/logo-wordmark.png").then(setWordmark).catch(() => setWordmark(null));
    }, []);
    useEffect(() => {
        const src = profile?.shop_logo_url || profile?.shop_photo_url;
        if (!src) { setShopLogo(null); return; }
        loadImg(publicAssetUrl(src)).then(setShopLogo).catch(() => setShopLogo(null));
    }, [profile?.shop_logo_url, profile?.shop_photo_url]);

    // Re-seed the discount from Step 1 whenever it changes upstream, unless
    // the merchant has manually overridden it. Never overwrites a merchant edit.
    useEffect(() => {
        if (discountEditedRef.current) return;
        setState((s) => (s.discount === (initialDiscount || "") ? s : { ...s, discount: initialDiscount || "" }));
    }, [initialDiscount]);

    // When template changes, reset editable defaults but keep the discount + photo
    const swapTemplate = (nextId) => {
        const nt = TEMPLATE_MAP[nextId];
        setTemplateId(nextId);
        setState((s) => ({ ...s, headline: nt.defaults.headline, sub: nt.defaults.sub, accent: nt.defaults.accent, font: nt.defaults.font }));
    };

    // Re-render on any relevant change
    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        c.width = CANVAS_W; c.height = CANVAS_H;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        tpl.draw(ctx, { ...state, photo: productPhoto });
        // Phase 10 — sticker layer sits between the composition and branding
        for (const s of stickers) drawSticker(ctx, s, CANVAS_W, CANVAS_H);
        drawBranding(ctx, wordmark, shopLogo, profile?.business_name);
        // Selection outline (canvas-only visual; not exported since we redraw
        // right before publish without it)
        if (selectedStickerId) {
            const s = stickers.find((x) => x.id === selectedStickerId);
            if (s) {
                const w = s.scale * CANVAS_W;
                ctx.save();
                ctx.strokeStyle = "#FF7A1A";
                ctx.lineWidth = 6;
                ctx.setLineDash([16, 12]);
                ctx.strokeRect(s.x_percent * CANVAS_W - w / 2, s.y_percent * CANVAS_H - w / 2, w, w);
                ctx.restore();
            }
        }
        setGeneratedUrl(null); // reset when the picture changes
    }, [templateId, state, productPhoto, wordmark, shopLogo, tpl, profile?.business_name, stickers, selectedStickerId]);

    const onPhotoChosen = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) return toast.error("Product photo must be under 8 MB");
        const url = URL.createObjectURL(f);
        setProductSrc(url);
        try { setProductPhoto(await loadImg(url)); }
        catch { toast.error("Couldn't load that image"); }
    };

    const publish = async () => {
        const c = canvasRef.current;
        if (!c) return;
        setUploading(true);
        try {
            // Redraw without selection outline for the final export
            const ctx = c.getContext("2d");
            ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
            tpl.draw(ctx, { ...state, photo: productPhoto });
            for (const s of stickers) drawSticker(ctx, s, CANVAS_W, CANVAS_H);
            drawBranding(ctx, wordmark, shopLogo, profile?.business_name);

            const blob = await new Promise((res) => c.toBlob(res, "image/png", 0.92));
            if (!blob) throw new Error("Canvas export failed");
            const fd = new FormData();
            fd.append("file", new File([blob], `poster-${Date.now()}.png`, { type: "image/png" }));
            fd.append("purpose", "poster");
            const up = await apiClient.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = up.data?.url;
            if (!url) throw new Error("Upload returned no URL");
            setGeneratedUrl(url);
            onGenerated?.(url);
            toast.success("Poster generated ✓");
        } catch (e) {
            toast.error(errorMessage(e));
        } finally { setUploading(false); }
    };

    // Phase 10 — sticker helpers
    const stickersRef = useRef([]);
    useEffect(() => { stickersRef.current = stickers; }, [stickers]);

    const canvasCoords = (evt) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const clientX = evt.clientX ?? evt.touches?.[0]?.clientX ?? 0;
        const clientY = evt.clientY ?? evt.touches?.[0]?.clientY ?? 0;
        return {
            x: ((clientX - rect.left) / rect.width) * CANVAS_W,
            y: ((clientY - rect.top) / rect.height) * CANVAS_H,
        };
    };
    const addSticker = (tpl) => {
        if (stickers.length >= MAX_STICKERS) { toast.error(`Max ${MAX_STICKERS} stickers`); return; }
        const id = `st_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        // Auto-fill starburst text with the current discount if set
        let text = tpl.text;
        if (tpl.kind === "STARBURST" && state.discount) text = state.discount;
        const next = {
            id, kind: tpl.kind, text, color: tpl.color,
            x_percent: 0.5, y_percent: 0.5, scale: DEFAULT_SCALE,
        };
        setStickers((prev) => [...prev, next]);
        setSelectedStickerId(id);
    };
    const applyPreset = (preset) => {
        if (preset.stickers.length + stickers.length > MAX_STICKERS) {
            setStickers([]); // replace
        }
        const withIds = preset.stickers.map((s, i) => ({
            ...s, id: `st_${Date.now()}_${i}`,
            text: s.kind === "STARBURST" && state.discount ? state.discount : s.text,
        }));
        setStickers((prev) => (withIds.length + prev.length > MAX_STICKERS ? withIds : [...prev, ...withIds]));
        setSelectedStickerId(null);
    };
    const updateSticker = (id, patch) => setStickers((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const deleteSticker = (id) => {
        setStickers((prev) => prev.filter((s) => s.id !== id));
        if (selectedStickerId === id) setSelectedStickerId(null);
    };

    const onCanvasPointerDown = (evt) => {
        const list = stickersRef.current;
        if (!list.length) return;
        const { x, y } = canvasCoords(evt);
        // Hit-test top-most first
        for (let i = list.length - 1; i >= 0; i--) {
            const s = list[i];
            if (stickerHit(s, CANVAS_W, CANVAS_H, x, y)) {
                dragRef.current = {
                    id: s.id,
                    offsetX: x - s.x_percent * CANVAS_W,
                    offsetY: y - s.y_percent * CANVAS_H,
                    moved: false,
                };
                setSelectedStickerId(s.id);
                try { evt.currentTarget.setPointerCapture(evt.pointerId); } catch { /* no-op */ }
                evt.preventDefault();
                return;
            }
        }
        setSelectedStickerId(null);
    };
    const onCanvasPointerMove = (evt) => {
        if (!dragRef.current.id) return;
        const { x, y } = canvasCoords(evt);
        const nx = (x - dragRef.current.offsetX) / CANVAS_W;
        const ny = (y - dragRef.current.offsetY) / CANVAS_H;
        dragRef.current.moved = true;
        updateSticker(dragRef.current.id, {
            x_percent: Math.min(0.95, Math.max(0.05, nx)),
            y_percent: Math.min(0.95, Math.max(0.05, ny)),
        });
    };
    const onCanvasPointerUp = () => { dragRef.current = { id: null, offsetX: 0, offsetY: 0, moved: false }; };

    const selected = stickers.find((s) => s.id === selectedStickerId) || null;

    const preview = (
        <div className="mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50 shadow-inner" data-testid="poster-preview-wrap">
            <canvas
                ref={canvasRef}
                className="block h-auto w-full touch-none"
                data-testid="poster-canvas"
                onPointerDown={onCanvasPointerDown}
                onPointerMove={onCanvasPointerMove}
                onPointerUp={onCanvasPointerUp}
                onPointerCancel={onCanvasPointerUp}
            />
        </div>
    );

    return (
        <div className="grid gap-4 lg:grid-cols-12" data-testid="poster-studio">
            {/* Template gallery */}
            <div className="lg:col-span-3">
                <div className="mb-2 text-xs font-bold uppercase text-neutral-500">Templates</div>
                <div className="flex gap-2 overflow-x-auto pb-2 lg:grid lg:grid-cols-1" data-testid="template-gallery">
                    {TEMPLATE_LIST.map((t) => (
                        <button
                            type="button"
                            key={t.id}
                            onClick={() => swapTemplate(t.id)}
                            data-testid={`tpl-${t.id}`}
                            className={`shrink-0 w-32 lg:w-full rounded-xl border-2 p-2 text-left transition ${templateId === t.id ? "border-orange-500 bg-orange-50" : "border-neutral-200 bg-white hover:border-orange-300"}`}
                        >
                            <div className="mb-1 h-16 w-full rounded-md" style={{ background: `linear-gradient(135deg, ${t.defaults.accent} 0%, #1a1a1a 100%)` }} />
                            <div className="text-xs font-bold text-neutral-900 truncate">{t.name}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Canvas preview */}
            <div className="lg:col-span-5">
                {preview}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    {generatedUrl ? (
                        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="poster-generated-pill">
                            <CheckCircle2 size={12} /> Poster generated
                        </div>
                    ) : <div />}
                    <Button onClick={publish} disabled={uploading} className="nest-gradient text-white" data-testid="poster-generate-btn">
                        <Sparkles size={14} className="mr-2" /> {uploading ? "Generating…" : generatedUrl ? "Regenerate" : "Generate poster"}
                    </Button>
                </div>
            </div>

            {/* Controls */}
            <div className="lg:col-span-4">
                <div className="mb-2 text-xs font-bold uppercase text-neutral-500">Design</div>
                <div className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-4">
                    <div>
                        <Label className="text-xs">Product photo</Label>
                        <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-neutral-300 p-3 text-xs hover:bg-neutral-50" data-testid="poster-photo-label">
                            <Upload size={14} /> {productSrc ? "Change photo" : "Upload / capture"}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoChosen} data-testid="poster-photo-input" />
                        </label>
                        {productSrc && (
                            <div className="mt-2">
                                <Label className="text-xs">Zoom</Label>
                                <Slider value={[state.zoom * 100]} min={80} max={200} step={5} onValueChange={([v]) => setState((s) => ({ ...s, zoom: v / 100 }))} data-testid="poster-zoom" />
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs">Headline</Label>
                        <Input value={state.headline} onChange={(e) => setState((s) => ({ ...s, headline: e.target.value }))} maxLength={40} data-testid="poster-headline" />
                    </div>
                    <div>
                        <Label className="text-xs">Discount / highlight</Label>
                        <Input value={state.discount} onChange={(e) => setDiscount(e.target.value)} placeholder="e.g. 20% OFF or ₹100 OFF" maxLength={40} data-testid="poster-discount" />
                    </div>
                    <div>
                        <Label className="text-xs">Sub-line (optional)</Label>
                        <Input value={state.sub} onChange={(e) => setState((s) => ({ ...s, sub: e.target.value }))} maxLength={60} data-testid="poster-sub" />
                    </div>
                    <div>
                        <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-neutral-600"><Palette size={12} /> Accent</div>
                        <div className="flex gap-2" data-testid="poster-accent-row">
                            {tpl.palette.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setState((s) => ({ ...s, accent: c }))}
                                    data-testid={`poster-accent-${c.replace("#", "")}`}
                                    className={`h-8 w-8 rounded-full border-2 ${state.accent === c ? "border-neutral-900" : "border-white"} shadow`}
                                    style={{ background: c }}
                                    aria-label={c}
                                />
                            ))}
                            <Input
                                type="color"
                                value={state.accent}
                                onChange={(e) => setState((s) => ({ ...s, accent: e.target.value }))}
                                className="h-8 w-14 cursor-pointer p-0"
                                data-testid="poster-accent-custom"
                            />
                        </div>
                    </div>
                    <div>
                        <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-neutral-600"><TypeIcon size={12} /> Font style</div>
                        <div className="flex gap-2" data-testid="poster-font-row">
                            {FONT_STYLES.map((f) => (
                                <button
                                    key={f.key}
                                    type="button"
                                    onClick={() => setState((s) => ({ ...s, font: f.key }))}
                                    data-testid={`poster-font-${f.key}`}
                                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${state.font === f.key ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white text-neutral-700"}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Phase 10 — Sticker library */}
                    <div className="border-t border-neutral-200 pt-3" data-testid="sticker-panel">
                        <div className="mb-1 flex items-center justify-between text-xs font-semibold text-neutral-600">
                            <span className="flex items-center gap-1"><StickerIcon size={12} /> Stickers</span>
                            <span className="text-[10px] text-neutral-400">{stickers.length}/{MAX_STICKERS}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-1" data-testid="sticker-library">
                            {STICKER_LIBRARY.map((t) => (
                                <button
                                    key={t.kind}
                                    type="button"
                                    onClick={() => addSticker(t)}
                                    data-testid={`sticker-add-${t.kind}`}
                                    disabled={stickers.length >= MAX_STICKERS}
                                    className="rounded-lg border border-neutral-200 bg-white p-2 text-[10px] font-bold text-neutral-800 transition hover:border-orange-400 hover:bg-orange-50 disabled:opacity-40"
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-2">
                            <div className="mb-1 text-[10px] font-semibold uppercase text-neutral-500">Preset examples</div>
                            <div className="flex flex-wrap gap-1" data-testid="sticker-presets">
                                {STICKER_PRESETS.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => applyPreset(p)}
                                        data-testid={`sticker-preset-${p.id}`}
                                        className="rounded-full border border-orange-200 bg-orange-50 px-2 py-1 text-[10px] font-semibold text-orange-700 hover:bg-orange-100"
                                    >
                                        {p.label}
                                    </button>
                                ))}
                                {stickers.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => { setStickers([]); setSelectedStickerId(null); }}
                                        data-testid="sticker-clear-all"
                                        className="ml-auto rounded-full border border-neutral-300 bg-white px-2 py-1 text-[10px] font-semibold text-neutral-600 hover:border-red-400 hover:text-red-600"
                                    >
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Mini-editor for the selected sticker */}
                        {selected && (
                            <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 p-3" data-testid="sticker-editor">
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-[11px] font-bold uppercase text-orange-700">Edit sticker</span>
                                    <button type="button" onClick={() => setSelectedStickerId(null)} data-testid="sticker-editor-close" className="text-neutral-500 hover:text-neutral-800">
                                        <X size={14} />
                                    </button>
                                </div>
                                <Label className="text-xs">Text</Label>
                                <Input
                                    value={selected.text}
                                    onChange={(e) => updateSticker(selected.id, { text: e.target.value })}
                                    maxLength={20}
                                    className="mb-2"
                                    data-testid="sticker-editor-text"
                                />
                                <Label className="text-xs">Size</Label>
                                <Slider
                                    value={[Math.round(selected.scale * 100)]}
                                    min={Math.round(MIN_SCALE * 100)} max={Math.round(MAX_SCALE * 100)} step={1}
                                    onValueChange={([v]) => updateSticker(selected.id, { scale: v / 100 })}
                                    data-testid="sticker-editor-size"
                                />
                                <div className="mt-2">
                                    <Label className="text-xs">Color</Label>
                                    <div className="mt-1 flex gap-1" data-testid="sticker-editor-colors">
                                        {PRESET_COLORS.map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => updateSticker(selected.id, { color: c })}
                                                data-testid={`sticker-color-${c.replace("#", "")}`}
                                                className={`h-7 w-7 rounded-full border-2 shadow ${selected.color === c ? "border-neutral-900" : "border-white"}`}
                                                style={{ background: c }}
                                                aria-label={c}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => deleteSticker(selected.id)}
                                    data-testid="sticker-editor-delete"
                                    className="mt-3 inline-flex items-center gap-1 rounded-full border border-red-300 bg-white px-3 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 size={12} /> Delete sticker
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PosterStudio;
